import { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { Input } from './ui/input';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Global flag to track if script is loading/loaded
let googleMapsScriptLoading = false;
let googleMapsScriptLoaded = false;
const scriptLoadCallbacks = [];

const loadGoogleMapsScript = (callback) => {
  // If already loaded, call callback immediately
  if (googleMapsScriptLoaded && window.google?.maps?.places) {
    callback();
    return;
  }
  
  // Add to callbacks queue
  scriptLoadCallbacks.push(callback);
  
  // If already loading, just wait for it
  if (googleMapsScriptLoading) {
    return;
  }
  
  // Start loading
  googleMapsScriptLoading = true;
  
  // Check if script already exists in DOM
  if (window.google?.maps?.places) {
    googleMapsScriptLoaded = true;
    scriptLoadCallbacks.forEach(cb => cb());
    scriptLoadCallbacks.length = 0;
    return;
  }
  
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
  script.async = true;
  script.onload = () => {
    googleMapsScriptLoaded = true;
    scriptLoadCallbacks.forEach(cb => cb());
    scriptLoadCallbacks.length = 0;
  };
  document.head.appendChild(script);
};

export const LocationAutocomplete = ({ 
  value, 
  onChange, 
  onSelect,
  placeholder = "Enter suburb or postcode...",
  className = ""
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [userHasEdited, setUserHasEdited] = useState(false);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Load Google Maps script using centralized loader
    if (GOOGLE_MAPS_API_KEY) {
      loadGoogleMapsScript(initServices);
    }
  }, []);

  const initServices = () => {
    if (window.google?.maps?.places) {
      // Use the new AutocompleteSuggestion API (recommended as of March 2025)
      // Falls back to AutocompleteService if the new API is not available
      if (window.google.maps.places.AutocompleteSuggestion) {
        // New API is available - we'll use fetchAutocompleteSuggestions in handleInputChange
        autocompleteService.current = 'new_api';
      } else {
        // Fallback to old API for backwards compatibility
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
      }
      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
    }
  };

  useEffect(() => {
    // Only update from external value if user hasn't started editing
    if (!userHasEdited && value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value]);

  const handleInputChange = async (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setUserHasEdited(true);
    onChange?.(newValue);

    if (newValue.length > 2 && autocompleteService.current) {
      setLoading(true);
      
      try {
        // Use new API if available
        if (autocompleteService.current === 'new_api' && window.google.maps.places.AutocompleteSuggestion) {
          const request = {
            input: newValue,
            includedRegionCodes: ['au']
          };
          
          // Use the new fetchAutocompleteSuggestions method
          const { suggestions: autocompleteSuggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
          
          if (autocompleteSuggestions && autocompleteSuggestions.length > 0) {
            // Convert new API format to old format for compatibility
            const predictions = autocompleteSuggestions.map(suggestion => ({
              description: suggestion.placePrediction.text.text,
              place_id: suggestion.placePrediction.placeId,
              structured_formatting: {
                main_text: suggestion.placePrediction.structuredFormat?.mainText?.text || suggestion.placePrediction.text.text,
                secondary_text: suggestion.placePrediction.structuredFormat?.secondaryText?.text || ''
              }
            }));
            setSuggestions(predictions);
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        } else if (autocompleteService.current?.getPlacePredictions) {
          // Fallback to old API
          autocompleteService.current.getPlacePredictions(
            {
              input: newValue,
              componentRestrictions: { country: 'au' },
              types: ['(regions)'], // Suburbs, cities, postcodes
            },
            (predictions, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                setSuggestions(predictions);
                setShowSuggestions(true);
              } else {
                setSuggestions([]);
                setShowSuggestions(false);
              }
            }
          );
        }
      } catch (error) {
        console.error('Autocomplete error:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setLoading(false);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setInputValue(suggestion.description);
    setShowSuggestions(false);
    setSuggestions([]);
    setUserHasEdited(false); // Reset after selection

    // Get place details for coordinates
    if (placesService.current) {
      placesService.current.getDetails(
        { placeId: suggestion.place_id, fields: ['geometry', 'formatted_address', 'name'] },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            const locationData = {
              name: suggestion.description,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
              placeId: suggestion.place_id,
            };
            onSelect?.(locationData);
          }
        }
      );
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fallback if Google Maps API key not available
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className={`relative ${className}`}>
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C9956C]" />
        <Input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            onChange?.(e.target.value);
          }}
          placeholder={placeholder}
          className="pl-10 bg-[var(--theme-card)] border-[var(--theme-border)] text-[var(--theme-text-primary)]"
        />
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={inputRef}>
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C9956C]" />
      <Input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        placeholder={placeholder}
        className="pl-10 bg-[var(--theme-card)] border-[var(--theme-border)] text-[var(--theme-text-primary)]"
      />
      {loading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[var(--theme-text-muted)]" />
      )}
      
      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.place_id}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className="w-full text-left px-4 py-3 hover:bg-[var(--theme-hover)] transition-colors flex items-start gap-3 border-b border-[var(--theme-border)] last:border-b-0"
            >
              <MapPin className="w-4 h-4 text-[#C9956C] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-[var(--theme-text-primary)]">
                  {suggestion.structured_formatting?.main_text || suggestion.description}
                </p>
                {suggestion.structured_formatting?.secondary_text && (
                  <p className="text-xs text-[var(--theme-text-muted)]">
                    {suggestion.structured_formatting.secondary_text}
                  </p>
                )}
              </div>
            </button>
          ))}
          <div className="px-4 py-2 text-xs text-[var(--theme-text-muted)] bg-[var(--theme-hover)]">
            Powered by Google
          </div>
        </div>
      )}
    </div>
  );
};
